// Full component with GIA and LOA document support per user
import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Container,
  Paper,
  Box,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Checkbox,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import { uploadGIA, uploadLOA } from "../../../../api/bondsClient";
import { useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import { useNavigate } from "react-router-dom";
import LawFirmUserFormFields from "./components/LawFirmUserFormFields";
import {
  buildLawFirmUserPreferredName,
  createEmptyLawFirmUser,
  LAW_FIRM_USER_POSITION_OPTIONS,
} from "./lawFirmLocationFormConfig";

const positionOptions = LAW_FIRM_USER_POSITION_OPTIONS;

function LegalServiceProfessionalForm() {
  const { id } = useParams();
  const [locations, setLocations] = useState([]);
  const [lawSchools, setLawSchools] = useState([]);
  const [legalServiceProfessionals, setLegalServiceProfessionals] = useState(
    []
  );
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [emailExists, setEmailExists] = useState([]);
  const [emailChecking, setEmailChecking] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState([]);

  const navigate = useNavigate();

  const defaultUser = createEmptyLawFirmUser();

  const [users, setUsers] = useState([defaultUser]);

  // Debounced function to check email existence
  const debouncedCheckEmail = React.useRef(
    debounce(async (email, idx) => {
      if (!email) {
        setEmailExists((prev) => {
          const arr = [...prev];
          arr[idx] = false;
          return arr;
        });
        setEmailChecking((prev) => {
          const arr = [...prev];
          arr[idx] = false;
          return arr;
        });
        setEmailDialogOpen((prev) => {
          const arr = [...prev];
          arr[idx] = false;
          return arr;
        });
        return;
      }
      setEmailChecking((prev) => {
        const arr = [...prev];
        arr[idx] = true;
        return arr;
      });
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists((prev) => {
          const arr = [...prev];
          arr[idx] = false;
          return arr;
        });
      } catch (e) {
        if (e && e.detail === "Email already exists") {
          setEmailDialogOpen((prev) => {
            const arr = [...prev];
            arr[idx] = true;
            return arr;
          });
          setEmailExists((prev) => {
            const arr = [...prev];
            arr[idx] = true;
            return arr;
          });
        } else {
          setEmailExists((prev) => {
            const arr = [...prev];
            arr[idx] = false;
            return arr;
          });
        }
      } finally {
        setEmailChecking((prev) => {
          const arr = [...prev];
          arr[idx] = false;
          return arr;
        });
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  // Update emailExists, emailChecking, emailDialogOpen, pendingEmail arrays when users array changes
  useEffect(() => {
    setEmailExists((prev) => users.map((_, i) => prev[i] || false));
    setEmailChecking((prev) => users.map((_, i) => prev[i] || false));
    setEmailDialogOpen((prev) => users.map((_, i) => prev[i] || false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.length]);

  const handleUserInputChange = (index, field, value) => {
    const updatedUsers = [...users];
    updatedUsers[index] = { ...updatedUsers[index], [field]: value };
    if (field === "position") {
      updatedUsers[index].isLegalService = false;
      updatedUsers[index].selectedProfessional = "";
      updatedUsers[index].showCustomForm = false;
    }
    setUsers(updatedUsers);
    if (field === "email") {
      debouncedCheckEmail(value, index);
    }
  };

  const handleAddUser = () => {
    setUsers([...users, { ...defaultUser }]);
  };

  const handleRemoveUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [locationsResponse, lawSchoolsResponse, professionalsResponse] =
          await Promise.all([
            apiClient(`/get-locations-by-lawfirm/${id}`, "GET"),
            apiClient(`/lawschools/`, "GET"),
            apiClient(`/legal-service-paralegals/`, "GET"),
          ]);

        setLocations(locationsResponse);
        setLawSchools(lawSchoolsResponse.data);
        setLegalServiceProfessionals(
          professionalsResponse.map((pro) => ({
            id: pro.lawfirm_user_id,
            name: `${pro.preferred_name}`,
          }))
        );
      } catch (error) {
        toast.error("Error loading data");
        setLegalServiceProfessionals([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const buildPreferredName = buildLawFirmUserPreferredName;

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      const submissions = users.map(async (user) => {
        let gia_document_link = user.gia_document_link;
        let gia_document_name = user.gia_document_name;
        let loa_document_link = user.loa_document_link;
        let loa_document_name = user.loa_document_name;

        if (
          user.position === "Attorney" &&
          user.gia_file &&
          user.is_gia_on_file
        ) {
          const uploadResponse = await uploadGIA(user.gia_file);
          gia_document_link = uploadResponse.file_url;
          gia_document_name = uploadResponse.file_name;
        }

        if (
          user.position === "Attorney" &&
          user.loa_file &&
          user.is_loa_on_file
        ) {
          const uploadResponse = await uploadLOA(user.loa_file);
          loa_document_link = uploadResponse.file_url;
          loa_document_name = uploadResponse.file_name;
        }

        if (user.selectedProfessional) {
          return apiClient("/create-lawfirm-location-users", "POST", {
            lawfirm_id: id,
            law_firm_location_id: user.location,
            lawfirm_user_id: user.selectedProfessional,
          });
        }

        return apiClient("/invite-lawfirm-user", "POST", {
          user: {
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
          },
          law_firm_location_id: user.location,
          lawfirm_id: id,
          title: user.position,
          position: user.position,
          middle_name: user.middleName,
          phone_number: user.phoneNumber,
          suffix: user.suffix,
          prefix: user.prefix,
          preferred_name: buildPreferredName(user),
          extension: user.extension,
          bar: user.barNumber,
          is_default: user.isDefault,
          is_legal_service: user.isLegalService,
          is_gia_on_file: user.is_gia_on_file,
          gia_document_link,
          gia_document_name,
          is_loa_on_file: user.is_loa_on_file,
          loa_document_link,
          loa_document_name,
          is_active: true,
          is_admin: user.permissionLevel === "Admin",
          lawschool: user.lawschool,
          send_invite: user.sendInvite,
          firm_email: user.useFirmEmail ? user.firmEmail : null,
        });
      });

      await Promise.all(submissions);
      const anyInvited = users.some((u) => u.sendInvite);
      toast.success(
        anyInvited
          ? "Users created and invites sent successfully"
          : "Users created successfully"
      );
      setUsers([defaultUser]);
      navigate(-1);
    } catch (error) {
      toast.error("Error submitting form");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Dialog handlers
  const handleDialogClose = (idx) => {
    setEmailDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  };
  const handleDialogContinue = (idx) => {
    setEmailDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
    setEmailExists((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  };
  const handleDialogCancel = (idx) => {
    setEmailDialogOpen((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
    setUsers((prev) => {
      const arr = [...prev];
      arr[idx] = { ...arr[idx], email: "" };
      return arr;
    });
    setEmailExists((prev) => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography variant="h5" sx={{ marginBottom: 2 }}>
            Create Law Firm User
          </Typography>

          {loading && (
            <CircularProgress sx={{ display: "block", margin: "0 auto" }} />
          )}

          {users.map((user, index) => (
            <Box
              key={index}
              sx={{ mb: 4, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">User {index + 1}</Typography>
                    {users.length > 1 && (
                      <IconButton
                        onClick={() => handleRemoveUser(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                {/* Location through permission fields */}
                <LawFirmUserFormFields
                  index={index}
                  user={user}
                  handleUserInputChange={handleUserInputChange}
                  locations={locations}
                  positionOptions={positionOptions}
                  lawSchools={lawSchools}
                  legalServiceProfessionals={legalServiceProfessionals}
                  emailExists={emailExists[index]}
                  emailChecking={emailChecking[index]}
                  showViewerPermission={false}
                />

                {/* GIA Section - Moved to the end of each user card */}
                {user.position === "Attorney" && (
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        GIA Document
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.is_gia_on_file}
                            onChange={(e) =>
                              handleUserInputChange(
                                index,
                                "is_gia_on_file",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Is GIA on file?"
                      />

                      {user.is_gia_on_file && (
                        <>
                          {user.gia_document_link ? (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body1">
                                Existing GIA Document:
                                <Link
                                  href={user.gia_document_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ ml: 1 }}
                                >
                                  {user.gia_document_name || "Download GIA"}
                                </Link>
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mb: 2 }}
                            >
                              No GIA document uploaded yet.
                            </Typography>
                          )}

                          <Box>
                            <input
                              accept=".pdf,.doc,.docx"
                              style={{ display: "none" }}
                              id={`gia-upload-${index}`}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const updatedUsers = [...users];
                                  updatedUsers[index].gia_file = file;
                                  updatedUsers[index].gia_document_name =
                                    file.name;
                                  setUsers(updatedUsers);
                                }
                              }}
                            />
                            <label htmlFor={`gia-upload-${index}`}>
                              <Button variant="contained" component="span">
                                {user.gia_file
                                  ? "Change GIA Document"
                                  : "Upload New GIA Document"}
                              </Button>
                            </label>
                            {user.gia_file && (
                              <Typography
                                variant="body2"
                                sx={{ ml: 2, display: "inline" }}
                              >
                                {user.gia_document_name}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}

                {user.position === "Attorney" && (
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        LOA Document
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={user.is_loa_on_file}
                            onChange={(e) =>
                              handleUserInputChange(
                                index,
                                "is_loa_on_file",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Is LOA on file?"
                      />

                      {user.is_loa_on_file && (
                        <>
                          {user.loa_document_link ? (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body1">
                                Existing LOA Document:
                                <Link
                                  href={user.loa_document_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ ml: 1 }}
                                >
                                  {user.loa_document_name || "Download LOA"}
                                </Link>
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mb: 2 }}
                            >
                              No LOA document uploaded yet.
                            </Typography>
                          )}

                          <Box>
                            <input
                              accept=".pdf,.doc,.docx"
                              style={{ display: "none" }}
                              id={`loa-upload-${index}`}
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const updatedUsers = [...users];
                                  updatedUsers[index].loa_file = file;
                                  updatedUsers[index].loa_document_name =
                                    file.name;
                                  setUsers(updatedUsers);
                                }
                              }}
                            />
                            <label htmlFor={`loa-upload-${index}`}>
                              <Button variant="contained" component="span">
                                {user.loa_file
                                  ? "Change LOA Document"
                                  : "Upload New LOA Document"}
                              </Button>
                            </label>
                            {user.loa_file && (
                              <Typography
                                variant="body2"
                                sx={{ ml: 2, display: "inline" }}
                              >
                                {user.loa_document_name}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* Send Invite Checkbox */}
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={user.sendInvite}
                        onChange={(e) =>
                          handleUserInputChange(
                            index,
                            "sendInvite",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Send Invite Email"
                  />
                </Grid>

                {/* Email Exists Dialog */}
                <Dialog
                  open={emailDialogOpen[index] || false}
                  onClose={(_, reason) => {
                    if (reason !== "backdropClick") handleDialogClose(index);
                  }}
                >
                  <DialogTitle>Email Already Exists</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      This email already exists. Would you like to continue with
                      this email?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => handleDialogCancel(index)}
                      color="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleDialogContinue(index)}
                      color="primary"
                      autoFocus
                    >
                      Continue
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
            </Box>
          ))}

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
            >
              Add Another User
            </Button>

            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Submit All Users"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setUsers([
                    {
                      prefix: "",
                      firstName: "",
                      lastName: "",
                      middleName: "",
                      email: "",
                      phoneNumber: "",
                      extension: "",
                      position: "",
                      permissionLevel: "User",
                      isLegalService: false,
                      isDefault: false,
                      lawschool: "",
                      barNumber: "",
                      selectedProfessional: "",
                      showCustomForm: false,
                      location: "",
                    },
                  ]);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default LegalServiceProfessionalForm;
